/* eslint-disable max-lines */
import { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import AlertCircleIcon from '@untitled-ui/icons-react/build/esm/AlertCircle';
import CalendarIcon from '@untitled-ui/icons-react/build/esm/Calendar';
import CheckCircleIcon from '@untitled-ui/icons-react/build/esm/CheckCircle';
import CheckVerifiedIcon from '@untitled-ui/icons-react/build/esm/CheckVerified01';

import { DatePicker, Input } from 'antd';
import { setDrawerCloseDisabled } from 'api/client-side-slices/DesignSlice';
import { setUboReportData } from 'api/client-side-slices/GroupsSlice';
import { useSearchIndividualByEmailMutation } from 'api/IndividualsApiSlice';
import { useAppDispatch, useAppSelector } from 'app-redux/hooks';
import { BaseButton } from 'components/Buttons';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import { OwnerWithEmail } from 'pages/KnowYourCustomer/AddNewCompany/AddNewCompanyFlow';
import {
  SelectedIndividual,
} from 'pages/KnowYourCustomer/AddNewIndividual/components';
import { Address, GetPersonModel, Owner } from 'solstice-types';
import { mobilePhoneRules, parseAddress, visibleMobilePhoneError } from 'utils';
import AddressInput from './AddressInput';
import FieldError from './FieldError';
import MobilePhoneInput from './MobilePhoneInput';
import { endOfToday, isAfter, isBefore } from 'date-fns';

dayjs.extend(weekday);
dayjs.extend(localeData);

interface FormData {
  email: string;
  mobilePhone?: string;
  dateOfBirth?: string;
  address?: Address;
}

interface TableRowComponentProps {
  record: any;
  userData?: Owner | null;
  index: number;
}

const EMPTY_MATCH_FIELDS = {
  mobilePhone: '',
  dateOfBirth: '',
  streetNumber: '',
  streetName: '',
  streetType: '',
  suburb: '',
  state: '',
  postcode: '',
  country: '',
};

const matchKeyOf = (match: GetPersonModel, index: number) => match.id ?? `match-${index}`;

export const TableRowComponent = ({ record, index }: TableRowComponentProps) => {
  const {
    control,
    formState: { errors, isValid, touchedFields, isSubmitted },
  } = useForm<FormData>({
    mode: 'onChange',
  });
  const [inputValues, setInputValues] = useState<{
    [key: string]: { email?: string; mobilePhone?: string; dateOfBirth?: string; address: Address };
  }>({});
  const [noMatchFound, setNoMatchFound] = useState(false);
  const [emailError, setEmailError] = useState<{ [key: string]: string }>({});
  const [selectedMatchKey, setSelectedMatchKey] = useState<string | null>(null);

  const { uboReportData } = useAppSelector((state) => state.groups);
  const dispatch = useAppDispatch();

  const formValues = useWatch({ control });

  const [searchIndividualByEmail, { data: emailMatches = [], isLoading, reset: resetSearch }] =
    useSearchIndividualByEmailMutation();

  const selectedMatch =
    emailMatches.find((match, i) => matchKeyOf(match, i) === selectedMatchKey) ?? null;

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const mobilePhoneError = visibleMobilePhoneError(
    errors.mobilePhone,
    !!touchedFields.mobilePhone,
    isSubmitted
  );

  const mobilePhoneCheck =
    noMatchFound ||
    (selectedMatch &&
      (selectedMatch.mobilePhone === undefined ||
        selectedMatch?.mobilePhone === '' ||
        selectedMatch?.mobilePhone == null));

  const dateOfBirthCheck =
    noMatchFound ||
    (selectedMatch &&
      (selectedMatch.dateOfBirth === undefined ||
        selectedMatch?.dateOfBirth === '' ||
        selectedMatch?.dateOfBirth === null)) ||
    selectedMatch?.dateOfBirth === '0001-01-01';

  const addressCheck =
    noMatchFound ||
    (selectedMatch &&
      (selectedMatch.address === undefined ||
        (selectedMatch?.address as string) === '' ||
        selectedMatch?.address === null));

  const handleInputChange = (key: string, field: 'email' | 'mobilePhone', value: string) => {
    setInputValues((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value,
      },
    }));

    if (field === 'email') {
      setEmailError((prev) => ({ ...prev, [key]: '' }));
      setSelectedMatchKey(null);
      setNoMatchFound(false);
      resetSearch();
      if (value && !validateEmail(value)) {
        setEmailError((prev) => ({ ...prev, [key]: 'Please enter a valid email address' }));
      }
    }
  };
  
  const applyMatch = (match: GetPersonModel | null, email: string) => {
    const parsedAddress = match?.address ? parseAddress(match.address as string) : {};

    const updatedOwnerData = match
      ? {
          ...EMPTY_MATCH_FIELDS,
          email,
          mobilePhone: match.mobilePhone || '',
          ...parsedAddress,
          dateOfBirth: match.dateOfBirth || '',
        }
      : { ...EMPTY_MATCH_FIELDS, email };

    const updatedUboReportData = {
      ...uboReportData,
      report: {
        ...uboReportData?.report,
        ubo: {
          ...uboReportData?.report?.ubo,
          beneficialOwners: {
            ...uboReportData?.report?.ubo?.beneficialOwners,
            owners: uboReportData?.report?.ubo?.beneficialOwners?.owners?.map((owner, i) =>
              i === index ? { ...owner, ...updatedOwnerData } : owner
            ),
          },
        },
      },
    };

    const allHaveEmails = updatedUboReportData.report.ubo.beneficialOwners.owners?.every(
      (owner: OwnerWithEmail) => owner.email && validateEmail(owner.email)
    );

    dispatch(setDrawerCloseDisabled(!allHaveEmails));
    dispatch(setUboReportData(updatedUboReportData));
  };

  const handleSelectMatch = (match: GetPersonModel, matchIndex: number) => {
    const email = inputValues[record.key]?.email;
    if (!email) return;

    setSelectedMatchKey(matchKeyOf(match, matchIndex));
    applyMatch(match, email);
  };

  const handleCheckMatch = async (key: string) => {
    const email = inputValues[key]?.email;

    if (!email || !validateEmail(email)) {
      setEmailError((prev) => ({ ...prev, [key]: 'Please enter a valid email address' }));
      return;
    }

    setSelectedMatchKey(null);

    try {
      const matches = await searchIndividualByEmail({ email }).unwrap();

      setNoMatchFound(matches.length === 0);

      if (matches.length === 1) {
        setSelectedMatchKey(matchKeyOf(matches[0], 0));
        applyMatch(matches[0], email);
      } else if (matches.length === 0) {
        applyMatch(null, email);
      }
    } catch (error) {
      console.error('Error fetching individual by email:', error);
      setNoMatchFound(true);
    }
  };

  useEffect(() => {
    if (!isValid) return;

    const updatedUboReportData = {
      ...uboReportData,
      report: {
        ...uboReportData?.report,
        ubo: {
          ...uboReportData?.report?.ubo,
          beneficialOwners: {
            ...uboReportData?.report?.ubo?.beneficialOwners,
            owners: uboReportData?.report?.ubo?.beneficialOwners?.owners?.map((owner, i) => {
              return i === index ? { ...owner, ...formValues } : owner;
            }),
          },
        },
      },
    };

    dispatch(setUboReportData(updatedUboReportData));
  }, [isValid, formValues]);
  
  return (
    <div className="flex flex-col">
      <div className="flex justify-between w-full gap-2.5">
        <div className="flex flex-col flex-grow">
          <Input
            placeholder="Enter email address..."
            value={inputValues[record.key]?.email || ''}
            onChange={(e) => handleInputChange(record.key, 'email', e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCheckMatch(record.key)}
            status={emailError[record.key] ? 'error' : ''}
            className="h-10 mb-2"
          />

          <FieldError message={emailError[record.key]} />
        </div>

        <div className="flex flex-col">
          <BaseButton
            className="min-w-[230px] mb-2"
            onClick={() => handleCheckMatch(record.key)}
            loading={isLoading}
            disabled={!inputValues[record.key]?.email}
          >
            <CheckVerifiedIcon />
            Check for Existing Match
          </BaseButton>
        </div>
      </div>
      <div className="flex flex-wrap w-full gap-2.5">
        <div className="flex flex-col">
          {' '}
          {/* This row has no submit, so the number holds its error back until the
              field is left rather than complaining part-way through typing. */}
          {mobilePhoneCheck && (
            <Controller
              name="mobilePhone"
              control={control}
              rules={mobilePhoneRules}
              render={({ field }) => (
                <MobilePhoneInput
                  {...field}
                  placeholder="Enter phone number..."
                  className="w-[385px]"
                  status={mobilePhoneError ? 'error' : ''}
                />
              )}
            />
          )}
          <FieldError message={mobilePhoneError?.message} />
        </div>

        {dateOfBirthCheck && (
          <Controller
            name="dateOfBirth"
            control={control}
            rules={{ required: 'Date of birth is required' }}
            render={({ field }) => (
              <DatePicker
                {...field}
                placeholder="Select date of birth..."
                format="DD/MM/YYYY"
                value={field.value ? dayjs(field.value) : null}
                onChange={(date) => field.onChange(date?.format('YYYY-MM-DD'))}
                suffixIcon={
                  <div className="border-l border-gray-300 h-10 flex items-center">
                    <CalendarIcon className="text-gray-700 h-5 w-5 ml-2.5" />
                  </div>
                }
                className="h-10 w-[230px]"
                disabledDate={(current: any) => {
                  const minDate = new Date(1900, 0, 1);
                  return (
                    current && (isAfter(current.toDate(), endOfToday()) || isBefore(current.toDate(), minDate))
                  );
                }}
              />
            )}
          />
        )}
        {addressCheck && (
          <AddressInput
            name="addressInput"
            control={control}
            placeholder="Enter Address..."
            className="w-[625px] h-10"
          />
        )}
      </div>

      {emailMatches.length === 1 && (
        <div className="flex text-success-700 gap-2 my-2">
          <CheckCircleIcon className="h-4 w-4" /> Check successful! Match found.
        </div>
      )}

      {emailMatches.length > 1 && (
        <div className="flex text-warning-700 gap-2 my-2">
          <AlertCircleIcon className="h-4 w-4 shrink-0" />
          {emailMatches.length} people share this email address. Select the right person.
        </div>
      )}

      {emailMatches.length > 0 && (
        <div className="flex flex-col gap-1">
          {emailMatches.map((match, matchIndex) => (
            <SelectedIndividual
              key={matchKeyOf(match, matchIndex)}
              user={match}
              searchQuery={''}
              isCheckingEmail
              isStatusVisible={false}
              hasDateOfBirth
              isSelected={matchKeyOf(match, matchIndex) === selectedMatchKey}
              onSelectIndividual={() => handleSelectMatch(match, matchIndex)}
            />
          ))}
        </div>
      )}

      {noMatchFound && (
        <div className="flex text-success-700 gap-2 mt-2">
          <CheckCircleIcon className="h-4 w-4" /> Check successful! No match found.
        </div>
      )}
    </div>
  );
};
